import { useEffect, useId, useRef, useState } from 'react';
import { DatePicker, DatePickerCalendar, DatePickerInput, DatePickerPopover, Span } from '@vp/swan';
import { __ } from '@99designs/i18n';

export interface AvailableDatePickerProps {
    /** The currently selected date, or null when nothing is selected. */
    selectedDate: Date | null;
    /**
     * Called with a valid, in-range, available date, or null when the field is cleared.
     * Invalid selections (out of range or unavailable) are not emitted; they surface an error instead.
     */
    onDateChange: (date: Date | null) => void;
    minDate?: Date;
    maxDate?: Date;
    isDateUnavailable?: (date: Date) => boolean;
    disabled?: boolean;
    /** Message shown when an invalid date is entered. */
    invalidDateMessage?: string;
    /**
     * Changing this value remounts the field, clearing any in-progress input and any error.
     * Use it to reset when upstream context changes (e.g. a timezone change).
     */
    resetKey?: string;
    'data-testid'?: string;
}

/**
 * A single-date picker for choosing an available appointment date.
 *
 * The DatePicker is uncontrolled (defaultDate) so typing a multi-digit year digit-by-digit
 * doesn't reset the field. Invalid selections (out of range or on an unavailable day) surface
 * an inline error and are never emitted, so callers only ever receive valid dates.
 */
export function AvailableDatePicker({
    selectedDate,
    onDateChange,
    minDate,
    maxDate,
    isDateUnavailable,
    disabled,
    invalidDateMessage,
    resetKey,
    'data-testid': dataTestId,
}: AvailableDatePickerProps) {
    const errorId = useId();
    const [invalid, setInvalid] = useState(false);

    // Clear any error when the field is reset from upstream.
    useEffect(() => setInvalid(false), [resetKey]);

    const defaultDate = selectedDate && !isNaN(selectedDate.getTime()) ? selectedDate : undefined;
    const selectedDayKey = toDayKey(defaultDate);

    // Uncontrolled field: remount to reflect programmatic date changes, but not our own emissions
    // (tracked below) so typing isn't interrupted. Compare by day, not exact time, for callers that
    // echo our date back through a date-only value.
    const lastEmittedDayRef = useRef<string | null>(selectedDayKey);
    const [syncCount, setSyncCount] = useState(0);
    useEffect(() => {
        if (selectedDayKey !== lastEmittedDayRef.current) {
            lastEmittedDayRef.current = selectedDayKey;
            setSyncCount((count) => count + 1);
            // The remount shows a new value, so drop any stale typing error.
            setInvalid(false);
        }
    }, [selectedDayKey]);

    return (
        <>
            <DatePicker
                key={`${resetKey}:${syncCount}`}
                data-testid={dataTestId}
                selectionMode="single"
                defaultDate={defaultDate}
                disabled={disabled}
                aria-errormessage={errorId}
                aria-describedby={errorId}
                minDate={minDate}
                maxDate={maxDate}
                isDateUnavailable={isDateUnavailable}
                onSelectedDateChange={(d) => {
                    if (!d) {
                        setInvalid(false);
                        // Record our emission so the echoed prop change won't remount.
                        lastEmittedDayRef.current = null;
                        onDateChange(null);
                        return;
                    }
                    const outOfRange = (minDate && d < minDate) || (maxDate && d > maxDate);
                    if (outOfRange || isDateUnavailable?.(d)) {
                        setInvalid(true);
                        return;
                    }
                    setInvalid(false);
                    lastEmittedDayRef.current = toDayKey(d);
                    onDateChange(d);
                }}
            >
                <DatePickerInput skin={invalid ? 'error' : undefined} />
                <DatePickerPopover>
                    <DatePickerCalendar />
                </DatePickerPopover>
            </DatePicker>
            {invalid && (
                <Span textColor="error" id={errorId}>
                    {invalidDateMessage ??
                        __('That date is not available. Please select an available date.')}
                </Span>
            )}
        </>
    );
}

// Local calendar-day identifier, ignoring the time component.
function toDayKey(date: Date | undefined): string | null {
    return date ? `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}` : null;
}
